/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { isEmpty } from 'lodash';
import { constructStreetValue, fullAddress } from '../../../../services/als';
import apiClient from '../../../../services/api-client';
import { DigitalFarmerAccount } from '../../../azure-b2c/storage';
import { ICampaignState } from './CampaignContext';
import {
  CampaignAgreementLine,
  CampaignDelivery,
  CampaignRequest,
  CampaignScheduleLine,
  FormikAgreementForm,
  FreightCalculateRequest,
  FreightCalculateResponse,
  MonthlyOrder,
  SaleAgreementPromo,
} from './types';
import { capitaliseFirstLetter, pluraliseUnit } from '../../../../util/consts';

export const convertToScheduleLines = (orders: MonthlyOrder[]): CampaignScheduleLine[] => {
  return orders.map((order) => ({
    quantity: order.quantity,
    releaseDate: new Date(order.year, order.month),
  }));
};

export const submitCampaignApi = async (
  value: FormikAgreementForm,
  state: ICampaignState,
  account: DigitalFarmerAccount,
  config: SaleAgreementPromo,
) => {
  if (!account) {
    throw new Error('Invalid info');
  }

  let delivery: CampaignRequest['delivery'] = null;

  if (state.deliveryOption?.type === 'Delivery' && state.addressSelected) {
    let _address: Pick<CampaignDelivery, 'deliveryAddress'> = {
      deliveryAddress: {
        fullAddress: '',
      },
    };

    if ('UniqueId' in state.addressSelected) {
      const completeAddress = await fullAddress(state.addressSelected.UniqueId);
      _address = {
        deliveryAddress: {
          fullAddress: completeAddress[0].FullAddress,
          city: completeAddress[0].TownCity,
          postcode: completeAddress[0].Postcode,
          streetAddress: constructStreetValue(completeAddress[0]),
          // I know this seems wrong, but I'm setting it anyway
          name: 'name' in state.addressSelected ? (state.addressSelected.name as string) : '',
        },
      };
    } else {
      const accountAddress = state.addressSelected;
      _address = {
        deliveryAddress: {
          fullAddress: accountAddress.fullAddress,
          city: accountAddress.city,
          postcode: accountAddress.zipCode,
          name: accountAddress.name,
          recordId: accountAddress.recordId,
          streetAddress: accountAddress.street,
        },
      };
    }

    delivery = {
      contactName: value.deliveryContactName,
      contactPhone: value.deliveryPhoneNum,
      deliveryAddress: {
        ..._address.deliveryAddress,
      },
      freightCost: {
        unitPrice: state.freightCharge,
        freightRange: state.freightRangeSelected
          ? {
              fromKm: state.freightRangeSelected?.fromKm,
              toKm: state.freightRangeSelected?.toKm,
            }
          : null,
      },
      hiabTruckRequired: value.hiabTruckRequired ?? false,
      notes: value.deliveryDetails,
      dairySupplyNumber: value.deliverySupplyNum,
      freightType: isEmpty(value.freightType) ? undefined : value.freightType,
      truck: isEmpty(value.truck) ? undefined : value.truck,
      carrier: isEmpty(value.carrier) ? undefined : value.carrier,
    };
  }

  const splitPayment = value.splitPayment
    ? {
        accountName: value.splitPaymentAccName,
        accountNumber: value.splitPaymentAccNumber,
        percentage: parseInt(value.splitPaymentContribution.toString()),
      }
    : undefined;

  const agreementLines: CampaignAgreementLine[] = state.productSelected.map((e) => {
    const line = {
      itemNumber: e.itemNumber,
      endDate: e.selectedDeliveryMonthPlan?.endDate ?? '',
      startDate: e.selectedDeliveryMonthPlan?.startDate ?? '',
      scheduleLines: convertToScheduleLines(e.orders ?? []).filter((e) => e.quantity > 0),
      totalQuantity: (e.orders ?? []).reduce((prev, current) => prev + current.quantity, 0),
      size: e.size ?? null,
      colour: e.colour ?? null,
      unitPrice: e.editedPrice ?? e.selectedDeliveryMonthPlan?.unitPrice ?? e.unitPrice,
      originalPrice: e.originalPrice,
      editedPrice: e.editedPrice,
      lastEditedReasonCode: e.lastEditedReasonCode,
      lastEditedReasonNote: e.lastEditedReasonNote,
      unitLabel: capitaliseFirstLetter(pluraliseUnit(e.unit, e.quantity)),
      feedRate: e.feedRate?.value ? Number(e.feedRate.value) : undefined,
      additives: e.additives,
    };
    return line;
  });

  const payload: CampaignRequest = {
    accountNumber: account.accountNumber,
    agreementLines: agreementLines,
    contact: {
      email: value.accEmail,
      phone: value.accPhoneNumber,
      name: value.accName,
      customerReference: value.reference,
    },
    siteId: state.siteSelected?.siteId ?? config.defaultSiteId,
    splitPayment: splitPayment,
    carrierCode: undefined,
    delivery: delivery,
    templateAgreementId: config.salesAgreementId,
  };

  return apiClient.post('/sales-agreements', payload);
};

export const freightCalculateApi = async (payload: FreightCalculateRequest) => {
  return await apiClient.post<FreightCalculateResponse, FreightCalculateRequest>(
    '/calculate-freight',
    payload,
  );
};
